<template>
  <div>
    <CheckboxWithLabel
      label="Require Multi-factor Authentication on the User Portal"
      :tooltip="$options.tooltip"
      :value="parent.enable_user_portal_multifactor === true"
      @change="handleRequireMFAChange"
    />
    <transition v-bind="transitionProps">
      <div
        v-show="isInitiallyInactive && parent.enable_user_portal_multifactor"
        :class="$style.enrollmentInputContainer"
      >
        <MFAEnrollmentInput
          v-model="mfaEnrollmentValue"
          :class="$style.enrollmentInput"
          keyword="this user"
        />
      </div>
    </transition>
    <Modal
      v-model="removeMFAModalVisible"
      cancelText=""
      :handleMainAction="handleRemoveMFAModalMainAction"
      headerText="Remove MFA Requirement"
      :kbLink="$options.tooltip.kbLink"
      mainActionText="ok"
    >
      <p :class="$style.modalBody">
        Removing the Multi-factor Authentication Requirement on User Portal option will not
        clear an existing TOTP token, but the user will no longer be required to
        provide a TOTP token or other second factor mechanism to complete their MFA setup,
        or verify with MFA for User Portal login or password resets. Users with an existing
        TOTP token will continue to be prompted for MFA on Systems that are MFA enabled. To
        completely remove the TOTP token for this user, you can Reset MFA before removing the
        MFA requirement for User Portal.
      </p>
    </Modal>
  </div>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { get } from 'lodash';
import { mapState } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import MFAEnrollmentInput from 'components/Modal/mfaModals/MFAEnrollmentInput.vue';
import Modal from 'components/Modal/Modal.vue';
import UserItemStates from 'components/mixins/UserItemStates';
import mfaEnrollmentMixin from 'components/mixins/mfaEnrollmentMixin';

export default {
  name: 'UserMFAStatus',

  components: {
    CheckboxWithLabel,
    MFAEnrollmentInput,
    Modal,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    mfaEnrollmentMixin,
    UserItemStates,
  ],

  inject: ['$validator'],

  props: {
    valueChange: {
      type: Function,
      required: true,
    },
  },

  data: () => ({
    removeMFAModalVisible: false,
  }),

  tooltip: {
    text: 'When you select this option, users are prompted to set up Time-based One-time Password (TOTP) MFA during the specified enrollment period. After TOTP MFA is set up, this user is required to enter a second factor mechanism when they log into the User Portal, applications and when they change their password. If you use Conditional Access Policies, they override User MFA settings.',
    kbLink: KbArticleUrls.mfaSetupForOrg,
  },

  computed: {
    ...mapState(StoreModelMap[ItemTypeConsts.systemUser], [
      'item',
      'previousItem',
    ]),

    isInitiallyInactive() {
      const mfaConfigured = get(this.previousItem, 'mfa.configured');
      return !this.previousItem.enable_user_portal_multifactor && !mfaConfigured;
    },

    transitionProps() {
      return {
        enterClass: this.$style.containerEnter,
        enterActiveClass: this.$style.containerTransition,
        leaveActiveClass: this.$style.containerTransition,
        leaveToClass: this.$style.containerLeaveTo,
      };
    },
  },

  watch: {
    mfaEnrollmentValue: function onChange() {
      this.updateExclusionFields();
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options, true);
  },

  methods: {
    handleRemoveMFAModalMainAction() {
      this.removeMFAModalVisible = false;
    },

    handleRequireMFAChange(checked) {
      this.valueChange(checked, 'enable_user_portal_multifactor');
      this.updateExclusionFields();

      // If the admin is disabling a user's MFA, display a warning notification
      if (this.previousItem.enable_user_portal_multifactor && !checked) {
        this.removeMFAModalVisible = true;
      }
    },

    updateExclusionFields() {
      // If initially disabled and enabling MFA on a user, set the exclusion attributes
      if (this.isInitiallyInactive && this.parent.enable_user_portal_multifactor) {
        const { state } = this.item;
        const payload = this.getMfaPayload(state);

        this.valueChange(payload, 'mfa');
      }

      // If initially disabled and setting back to disabled, revert exclusion attributes
      if (this.isInitiallyInactive && !this.parent.enable_user_portal_multifactor) {
        this.valueChange(this.previousItem.mfa, 'mfa');
      }
    },
  },
};
</script>

<style module>
@value (infoBlue) from '~css/colors.css';
@value (panelSpeedSm, panelTimingFunction) from '~css/transitions.css';
@value (fontHeight) from '~components/FormValidation/css/formFields.css';

.icon {
  font-size: fontHeight;
}

.enrollmentInput {
  font-weight: 400;
  margin: 0 5px 15px 25px;
}

.modalBody {
  font-size: 1em !important;
}

.enrollmentInputContainer {
  height: 38px;
}

.containerEnter,
.containerLeaveTo {
  height: 0;
  opacity: 0;
}

.containerTransition {
  transition: all panelSpeedSm panelTimingFunction;
}
</style>
